import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import Header from './components/Header';
import AnnouncementBanner from './components/AnnouncementBanner';
import Footer from './components/Footer';
import Logout from './pages/Logout';
import AuthError from './pages/AuthError';
import { authAPI } from './services/api';
import { AppProvider, useAppState, useAppDispatch, actions } from './context/AppContext';
import { redirectToSSO } from './utils/auth';
import './utils/chartSetup'; // Centralized Chart.js setup
import './styles/App.css';

// Code splitting: Lazy load large components
const Dashboard = lazy(() => import('./components/Dashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));

function AppContent() {
  const appState = useAppState();
  const dispatch = useAppDispatch();

  const isAuthErrorPage = window.location.pathname === '/auth-error';
  const [loading, setLoading] = useState<boolean>(!isAuthErrorPage);
  const isInitialMount = useRef<boolean>(true);

  const { currentUser: user, showLogoutPage, currentView } = appState;

  const checkAuth = async (): Promise<void> => {
    try {
      const response = await authAPI.getCurrentUser();
      if (response.data.authenticated) {
        dispatch(actions.setCurrentUser(response.data.user));
        setLoading(false);
      } else {
        // No session - auto-redirect to SSO login (keep loader visible)
        redirectToSSO();
      }
    } catch (err: any) {
      console.error('Auth check failed:', err);
      const status = err.response?.status;
      const detail = err.response?.data?.error || err.message || 'Unknown error';
      let message: string;
      if (!err.response) {
        message = 'Unable to reach the server. Please check your connection and try again.';
      } else if (status === 503) {
        message = 'The service is temporarily unavailable. Please try again shortly.';
      } else if (status >= 500) {
        message = `Server error during authentication (${status}). Please try again later.`;
      } else {
        message = `Authentication failed: ${detail}`;
      }
      window.location.href = `/auth-error?message=${encodeURIComponent(message)}`;
    }
  };

  useEffect(() => {
    // Skip auth check if on auth-error page to prevent redirect loop
    if (isAuthErrorPage) {
      return;
    }

    checkAuth();

    // Listen for session expiration events from API interceptor
    const handleSessionExpired = () => {
      console.log('Session expired, logging out user');
      dispatch(actions.setCurrentUser(null));
      dispatch(actions.setSearchEmail('', false));
      dispatch(actions.setShowLogoutPage(true));
      dispatch(actions.clearAllCache());
    };

    window.addEventListener('session-expired', handleSessionExpired);

    return () => {
      window.removeEventListener('session-expired', handleSessionExpired);
    };
  }, []); // checkAuth and dispatch are stable, safe to omit

  // Initialize URL on first load (must run before URL sync)
  useEffect(() => {
    if (!user) return;

    const path = window.location.pathname;

    if (path === '/admin') {
      if (user.isAdmin) {
        dispatch(actions.setCurrentView('admin'));
      } else {
        // Non-admin trying to access admin, redirect to home
        dispatch(actions.setCurrentView('dashboard'));
        window.history.replaceState({}, '', '/home');
      }
    } else if (path === '/auth-error') {
      // Don't redirect away from auth-error page
      return;
    } else if (path === '/home' || path === '/') {
      dispatch(actions.setCurrentView('dashboard'));
      if (path === '/') {
        window.history.replaceState({}, '', '/home');
      }
    } else {
      // Unknown path, redirect to home
      dispatch(actions.setCurrentView('dashboard'));
      window.history.replaceState({}, '', '/home');
    }

    // Mark that initial mount is complete
    isInitialMount.current = false;
  }, [user, dispatch]);

  // Sync URL with currentView (skip on initial mount)
  useEffect(() => {
    if (!user || isInitialMount.current) return;

    const path = currentView === 'admin' ? '/admin' : '/home';
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
    }
  }, [currentView, user]);

  // Handle browser back/forward navigation
  useEffect(() => {
    if (!user) return;

    const handlePopState = () => {
      const path = window.location.pathname;
      if (path === '/admin' && user.isAdmin) {
        dispatch(actions.setCurrentView('admin'));
      } else {
        dispatch(actions.setCurrentView('dashboard'));
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [user, dispatch]);

  return (
    <div className="app" data-testid="app-container">
      {isAuthErrorPage ? (
        <AuthError />
      ) : loading ? (
        <div className="loading" data-testid="app-loading-spinner">
          <div className="spinner"></div>
        </div>
      ) : user ? (
        <>
          {/* Admin-managed announcement banners, pinned above the header */}
          <AnnouncementBanner />
          <Header />
          <Suspense
            fallback={
              <div className="loading" data-testid="app-loading-spinner">
                <div className="spinner"></div>
              </div>
            }
          >
            {currentView === 'admin' && user.isAdmin ? <AdminDashboard /> : <Dashboard />}
          </Suspense>
          <Footer />
        </>
      ) : showLogoutPage ? (
        <Logout />
      ) : null}
    </div>
  );
}

// Wrap AppContent with AppProvider
function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}

export default App;
